import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link as RouterLink, useOutletContext } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  LinearProgress,
  Link,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import ReplayIcon from "@mui/icons-material/Replay";
import {
  getPlatformDominiosWhatsappServiceDailyBatch,
  listPlatformDominios,
} from "../../../api/client";
import { BusyButton } from "./shared";
import { PLATFORM_CONSOLE_PATH } from "./platformConsoleRoutes";
import {
  emptyWorkspaceI18nKey,
  failedWorkspaceIds,
  resolveWorkspaceLoadState,
  sortBoardRows,
  summarizeBoardRows,
  workspaceErrorI18nKey,
} from "./whatsappOut2026Board";

function useConsoleOutlet() {
  const ctx = useOutletContext() || {};
  return {
    registerRefresh: typeof ctx.registerRefresh === "function" ? ctx.registerRefresh : () => {},
    stopRefreshing: typeof ctx.stopRefreshing === "function" ? ctx.stopRefreshing : () => {},
    setGlobalError: typeof ctx.setGlobalError === "function" ? ctx.setGlobalError : () => {},
  };
}

function fmtQty(value, locale) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(locale, { maximumFractionDigits: n % 1 === 0 ? 0 : 1 });
}

function fmtMoney(value, locale) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString(locale, { style: "currency", currency: "BRL" });
}

function dayLabel(ymd) {
  const m = String(ymd || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? `${m[3]}/${m[2]}` : String(ymd || "");
}

function formatDateBr(ymd) {
  const m = String(ymd || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "—";
}

/** Abreviação do mês no estilo "set/2026", a partir de um YMD. */
function monthShortLabel(ymd, locale) {
  const m = String(ymd || "").match(/^(\d{4})-(\d{2})-\d{2}$/);
  if (!m) return "";
  const date = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, 1));
  const month = date.toLocaleDateString(locale || "pt-BR", { month: "short", timeZone: "UTC" });
  const cleaned = String(month || "")
    .replace(/\./g, "")
    .replace(/\u00a0/g, " ")
    .trim()
    .toLowerCase();
  return cleaned ? `${cleaned}/${m[1]}` : `${m[2]}/${m[1]}`;
}

function estimateSx(estimated) {
  if (!estimated) return {};
  return {
    fontStyle: "italic",
    color: "text.secondary",
    bgcolor: (th) => alpha(th.palette.warning.main, 0.14),
  };
}

function chunkErrorMessage(err) {
  return err?.response?.data?.message || err?.message || "";
}

const stickyNameSx = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  bgcolor: "background.paper",
  minWidth: 180,
  maxWidth: 260,
};

/**
 * Visão da plataforma: consumo diário de sessões WhatsApp por workspace,
 * total de cada dia e previsão de custo do mês.
 */
export default function WhatsappOut2026SectionPage() {
  const { t, i18n } = useTranslation("app");
  const locale = i18n.language || "pt-BR";
  const { registerRefresh, stopRefreshing, setGlobalError } = useConsoleOutlet();
  const [rows, setRows] = useState([]);
  const [month, setMonth] = useState(null);
  const [referencePeriod, setReferencePeriod] = useState(null);
  const [loadingList, setLoadingList] = useState(true);
  const [enriching, setEnriching] = useState(false);
  const [error, setError] = useState("");
  const genRef = useRef(0);
  const enrichingCountRef = useRef(0);
  const rowEpochRef = useRef(new Map());

  const bumpRowEpochs = useCallback((ids) => {
    const epochs = new Map();
    for (const raw of ids) {
      const id = Number(raw);
      const next = (rowEpochRef.current.get(id) || 0) + 1;
      rowEpochRef.current.set(id, next);
      epochs.set(id, next);
    }
    return epochs;
  }, []);

  const isCurrentEpoch = useCallback((id, epochs) => {
    const n = Number(id);
    return epochs.get(n) === rowEpochRef.current.get(n);
  }, []);

  const applyMonthMeta = useCallback((payload) => {
    if (payload?.month) setMonth(payload.month);
    if (payload?.reference_period) setReferencePeriod(payload.reference_period);
  }, []);

  const mergeHits = useCallback(
    (chunkItems, epochs) => {
      const byId = new Map((chunkItems || []).map((it) => [Number(it.dominio_id), it]));
      setRows((prev) => {
        const next = prev.map((row) => {
          const hit = byId.get(row.dominio_id);
          if (!hit || !isCurrentEpoch(row.dominio_id, epochs)) return row;
          return { ...row, ...hit, ...resolveWorkspaceLoadState(hit) };
        });
        return sortBoardRows(next);
      });
    },
    [isCurrentEpoch]
  );

  const markIds = useCallback(
    (ids, patch, epochs) => {
      const wanted = new Set((ids || []).map(Number));
      setRows((prev) => {
        let changed = false;
        const next = prev.map((row) => {
          if (!wanted.has(row.dominio_id) || !isCurrentEpoch(row.dominio_id, epochs)) return row;
          changed = true;
          return typeof patch === "function" ? patch(row) : { ...row, ...patch };
        });
        return changed ? sortBoardRows(next) : prev;
      });
    },
    [isCurrentEpoch]
  );

  const beginEnrich = useCallback(() => {
    enrichingCountRef.current += 1;
    setEnriching(true);
  }, []);

  const endEnrich = useCallback(
    (gen) => {
      enrichingCountRef.current = Math.max(0, enrichingCountRef.current - 1);
      if (enrichingCountRef.current === 0 && genRef.current === gen) {
        setEnriching(false);
        stopRefreshing();
      }
    },
    [stopRefreshing]
  );

  const enrichIds = useCallback(
    async (ids, gen) => {
      const clean = [...new Set((ids || []).map((x) => Number(x)).filter((n) => Number.isFinite(n) && n > 0))];
      if (!clean.length || genRef.current !== gen) return;
      const epochs = bumpRowEpochs(clean);
      beginEnrich();
      markIds(
        clean,
        (row) => ({
          dominio_id: row.dominio_id,
          dominio: row.dominio,
          status: "pending",
        }),
        epochs
      );
      try {
        await getPlatformDominiosWhatsappServiceDailyBatch(clean, {
          shouldStop: () => genRef.current !== gen,
          onChunk: (chunkItems, payload, chunkIds) => {
            if (genRef.current !== gen) return;
            applyMonthMeta(payload);
            mergeHits(chunkItems, epochs);
            const returned = new Set((chunkItems || []).map((it) => Number(it.dominio_id)));
            const missing = (chunkIds || []).filter((id) => !returned.has(Number(id)));
            if (missing.length) {
              markIds(
                missing,
                {
                  status: "error",
                  partner_ok: false,
                  partner_code: "chunk_missing",
                  partner_message: null,
                  days: [],
                },
                epochs
              );
            }
          },
          onChunkError: (failedIds, err) => {
            if (genRef.current !== gen) return;
            markIds(
              failedIds,
              {
                status: "error",
                partner_ok: false,
                partner_code: "chunk_failed",
                partner_message: chunkErrorMessage(err) || null,
                days: [],
              },
              epochs
            );
          },
        });
        if (genRef.current !== gen) return;
        markIds(
          clean,
          (row) =>
            row.status === "pending"
              ? {
                  ...row,
                  status: "error",
                  partner_ok: false,
                  partner_code: "chunk_missing",
                  days: [],
                }
              : row,
          epochs
        );
      } finally {
        endEnrich(gen);
      }
    },
    [applyMonthMeta, beginEnrich, bumpRowEpochs, endEnrich, markIds, mergeHits]
  );

  const load = useCallback(async () => {
    const gen = genRef.current + 1;
    genRef.current = gen;
    setLoadingList(true);
    setEnriching(false);
    setError("");
    setMonth(null);
    setReferencePeriod(null);
    try {
      const collected = [];
      let offset = 0;
      const limit = 500;
      for (;;) {
        const res = await listPlatformDominios({ limit, offset, tenantLegacyStats: "0" });
        const items = Array.isArray(res?.items) ? res.items : [];
        for (const d of items) {
          if (Number(d.excluido) === 1) continue;
          const id = Number(d.id);
          if (!Number.isFinite(id) || id <= 0) continue;
          collected.push({
            dominio_id: id,
            dominio: d.dominio || `#${id}`,
            status: "pending",
          });
        }
        const hasMore = res?.meta?.has_more === true || items.length >= limit;
        if (!hasMore || !items.length) break;
        offset += limit;
      }
      collected.sort((a, b) => String(a.dominio).localeCompare(String(b.dominio), "pt-BR"));
      if (genRef.current !== gen) return;
      setRows(collected);
      setLoadingList(false);
      if (!collected.length) {
        stopRefreshing();
        return;
      }
      await enrichIds(
        collected.map((r) => r.dominio_id),
        gen
      );
    } catch (err) {
      if (genRef.current !== gen) return;
      const msg = err?.response?.data?.message || err?.message || t("platformPage.waDailyBoardLoadError");
      setError(msg);
      setGlobalError(msg);
      setLoadingList(false);
      setEnriching(false);
      enrichingCountRef.current = 0;
      stopRefreshing();
    }
  }, [enrichIds, setGlobalError, stopRefreshing, t]);

  const retryIds = useCallback(
    (ids) => {
      const clean = [...new Set((ids || []).map(Number).filter((n) => Number.isFinite(n) && n > 0))];
      if (!clean.length) return;
      enrichIds(clean, genRef.current);
    },
    [enrichIds]
  );

  useEffect(() => {
    registerRefresh(() => {
      load();
    });
    return () => {
      genRef.current += 1;
      registerRefresh(() => stopRefreshing());
    };
  }, [load, registerRefresh, stopRefreshing]);

  useEffect(() => {
    load();
    return () => {
      genRef.current += 1;
    };
  }, [load]);

  const days = useMemo(() => {
    const byDate = new Map();
    for (const row of rows) {
      for (const day of row.days || []) {
        if (!day?.date) continue;
        const prev = byDate.get(day.date);
        if (!prev || (!day.projected && prev.projected)) {
          byDate.set(day.date, { date: day.date, projected: Boolean(day.projected) });
        }
      }
    }
    return [...byDate.values()].sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0));
  }, [rows]);

  const qtyByWorkspaceDay = useCallback((row, date) => {
    const hit = (row.days || []).find((d) => d.date === date);
    if (!hit) return null;
    return hit;
  }, []);

  const totals = useMemo(() => {
    const byDay = {};
    let forecast = 0;
    let projectedMessages = 0;
    let cost = 0;
    let forecastCount = 0;
    for (const row of rows) {
      if (row.status !== "ok") continue;
      forecast += Number(row.projected_amount_brl) || 0;
      projectedMessages += Number(row.projected_messages) || 0;
      cost += Number(row.cost_to_date_brl) || 0;
      forecastCount += 1;
      for (const day of row.days || []) {
        byDay[day.date] = (byDay[day.date] || 0) + (Number(day.qty) || 0);
      }
    }
    return { byDay, forecast, projectedMessages, cost, forecastCount };
  }, [rows]);

  const counts = useMemo(() => summarizeBoardRows(rows), [rows]);
  const errorIds = useMemo(() => failedWorkspaceIds(rows), [rows]);
  const metricColSpan = 3 + days.length;
  const progressValue = counts.total ? Math.round((counts.finished / counts.total) * 100) : 0;

  const usingReference = rows.some((r) => r.baseline === "reference_period");
  const hasClosedDay = days.some((day) => !day.projected);
  const closedDays = days.filter((day) => !day.projected);
  const estimateFrom = hasClosedDay ? closedDays[0]?.date : referencePeriod?.from;
  const estimateTo = hasClosedDay ? closedDays[closedDays.length - 1]?.date : referencePeriod?.to;
  const boardMonthLabel = monthShortLabel(month?.from || month?.to, locale);
  const referenceMonthLabel = monthShortLabel(referencePeriod?.from || referencePeriod?.to, locale);

  return (
    <Stack spacing={2}>
      <Box>
        <Typography variant="h6" fontWeight={700}>
          {t("platformPage.waDailyBoardTitle", {
            month: formatDateBr(month?.from).slice(3) || "",
          })}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 860, mt: 0.5 }}>
          {usingReference && !hasClosedDay
            ? t("platformPage.waDailyBoardHintEstimateOnly", {
                from: formatDateBr(referencePeriod?.from),
                to: formatDateBr(referencePeriod?.to),
                end: formatDateBr(month?.to),
                month: boardMonthLabel || "—",
                refMonth: referenceMonthLabel || "—",
              })
            : t("platformPage.waDailyBoardHint", {
                from: formatDateBr(month?.from),
                to: formatDateBr(month?.to),
                baseFrom: formatDateBr(estimateFrom),
                baseTo: formatDateBr(estimateTo),
              })}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 1.25 }} flexWrap="wrap" useFlexGap>
          <Chip size="small" variant="outlined" label={t("platformPage.waDailyBoardLegendActual")} />
          <Chip
            size="small"
            label={t("platformPage.waDailyBoardLegendEstimate")}
            sx={(th) => ({
              fontStyle: "italic",
              bgcolor: alpha(th.palette.warning.main, 0.14),
              color: th.palette.text.secondary,
            })}
          />
        </Stack>
      </Box>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
        <Paper variant="outlined" sx={{ p: 1.5, flex: 1, bgcolor: (th) => alpha(th.palette.warning.main, 0.08) }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {t("platformPage.waDailyBoardForecast")}
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {fmtMoney(totals.forecast, locale)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t("platformPage.waDailyBoardForecastMeta", {
              count: fmtQty(totals.forecastCount, locale),
              end: formatDateBr(month?.to),
            })}
          </Typography>
          {counts.error > 0 ? (
            <Typography variant="caption" color="warning.dark" display="block" sx={{ mt: 0.5, fontWeight: 700 }}>
              {t("platformPage.waDailyBoardPartialForecast", { count: counts.error })}
            </Typography>
          ) : null}
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.5, flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {t("platformPage.waDailyBoardCostToDate")}
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {fmtMoney(totals.cost, locale)}
          </Typography>
        </Paper>
        <Paper variant="outlined" sx={{ p: 1.5, flex: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            {t("platformPage.waDailyBoardProgress")}
          </Typography>
          <Typography variant="h5" fontWeight={800}>
            {fmtQty(counts.resolved, locale)}/{fmtQty(counts.total, locale)}
          </Typography>
          {enriching || counts.pending > 0 ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">
                {t("platformPage.waDailyBoardEnriching")}
              </Typography>
            </Stack>
          ) : null}
          {counts.error > 0 ? (
            <Typography variant="caption" color="error" display="block" sx={{ fontWeight: 700 }}>
              {t("platformPage.waDailyBoardProgressErrors", { count: counts.error })}
            </Typography>
          ) : null}
        </Paper>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {counts.error > 0 ? (
        <Alert severity="warning">
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
            justifyContent="space-between"
          >
            <Typography variant="body2">
              {t("platformPage.waDailyBoardErrorBanner", { count: counts.error })}
            </Typography>
            <BusyButton
              color="inherit"
              size="small"
              variant="outlined"
              startIcon={<ReplayIcon />}
              onClick={() => retryIds(errorIds)}
              sx={{ textTransform: "none", whiteSpace: "nowrap", flexShrink: 0 }}
            >
              {t("platformPage.waDailyBoardRetryFailed", { count: counts.error })}
            </BusyButton>
          </Stack>
        </Alert>
      ) : null}

      {loadingList ? (
        <Stack direction="row" spacing={1} alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={22} />
          <Typography variant="body2" color="text.secondary">
            {t("platformPage.waDailyBoardLoadingList")}
          </Typography>
        </Stack>
      ) : null}

      {!loadingList && rows.length === 0 ? (
        <Alert severity="info">{t("platformPage.waDailyBoardEmpty")}</Alert>
      ) : null}

      {rows.length > 0 ? (
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          {counts.pending > 0 || enriching ? (
            <LinearProgress
              variant="determinate"
              value={progressValue}
              sx={{ height: 3 }}
              aria-label={t("platformPage.waDailyBoardEnriching")}
            />
          ) : null}
          <TableContainer sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto" }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ ...stickyNameSx, zIndex: 3, fontWeight: 700 }}>
                    {t("platformPage.waDailyBoardColWorkspace")}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    {t("platformPage.waDailyBoardColBase", { month: boardMonthLabel || "—" })}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    {t("platformPage.waDailyBoardColPeriod", { month: boardMonthLabel || "—" })}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, whiteSpace: "nowrap" }}>
                    {t("platformPage.waDailyBoardColAvg")}
                  </TableCell>
                  {days.map((day) => (
                    <TableCell
                      key={day.date}
                      align="right"
                      title={
                        day.projected
                          ? t("platformPage.waDailyBoardProjectedMark")
                          : t("platformPage.waDailyBoardLegendActual")
                      }
                      sx={{
                        fontWeight: 700,
                        whiteSpace: "nowrap",
                        ...estimateSx(day.projected),
                      }}
                    >
                      {dayLabel(day.date)}
                      {day.projected ? (
                        <Typography
                          variant="caption"
                          display="block"
                          sx={{ fontStyle: "italic", fontWeight: 600, lineHeight: 1.1 }}
                        >
                          {t("platformPage.waDailyBoardEstTag")}
                        </Typography>
                      ) : null}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow sx={{ bgcolor: (th) => alpha(th.palette.primary.main, 0.06) }}>
                  <TableCell
                    sx={{ ...stickyNameSx, fontWeight: 800, bgcolor: (th) => alpha(th.palette.primary.main, 0.06) }}
                  >
                    {t("platformPage.waDailyBoardDayTotal")}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {fmtQty(totals.projectedMessages, locale)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontWeight: 800 }}>
                    {fmtMoney(totals.forecast, locale)}
                  </TableCell>
                  <TableCell />
                  {days.map((day) => (
                    <TableCell key={day.date} align="right" sx={{ fontWeight: 800, ...estimateSx(day.projected) }}>
                      {fmtQty(totals.byDay[day.date] || 0, locale)}
                    </TableCell>
                  ))}
                </TableRow>
                {rows.map((row) => {
                  const isError = row.status === "error";
                  const isPending = row.status === "pending";
                  const nameBg = isError
                    ? (th) => alpha(th.palette.error.main, 0.08)
                    : isPending
                      ? (th) => alpha(th.palette.action.hover, 0.6)
                      : stickyNameSx.bgcolor;
                  return (
                    <TableRow
                      key={row.dominio_id}
                      hover
                      sx={
                        isError
                          ? { bgcolor: (th) => alpha(th.palette.error.main, 0.05) }
                          : undefined
                      }
                    >
                      <TableCell sx={{ ...stickyNameSx, bgcolor: nameBg }}>
                        <Stack direction="row" spacing={0.5} alignItems="flex-start" justifyContent="space-between">
                          <Box sx={{ minWidth: 0 }}>
                            <Link
                              component={RouterLink}
                              to={`${PLATFORM_CONSOLE_PATH.workspaces}/${row.dominio_id}/whatsapp-pricing`}
                              underline="hover"
                              color="inherit"
                              fontWeight={600}
                            >
                              {row.dominio}
                            </Link>
                            {isPending ? (
                              <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                                <CircularProgress size={10} />
                                <Typography variant="caption" color="text.secondary">
                                  {t("platformPage.waDailyBoardPending")}
                                </Typography>
                              </Stack>
                            ) : null}
                            {row.status === "empty" ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                {t(emptyWorkspaceI18nKey(row.partner_code))}
                              </Typography>
                            ) : null}
                            {isError ? (
                              <Typography
                                variant="caption"
                                color="error"
                                display="block"
                                title={row.partner_message || undefined}
                                sx={{ fontWeight: 700 }}
                              >
                                {t(workspaceErrorI18nKey(row.partner_code))}
                              </Typography>
                            ) : null}
                          </Box>
                          {isError ? (
                            <Tooltip title={t("platformPage.waDailyBoardRetryOne")}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => retryIds([row.dominio_id])}
                                aria-label={t("platformPage.waDailyBoardRetryOne")}
                              >
                                <ReplayIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          ) : null}
                        </Stack>
                      </TableCell>
                      {isError || isPending ? (
                        <TableCell colSpan={metricColSpan}>
                          {isError ? (
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                              <Typography variant="body2" color="error">
                                {t("platformPage.waDailyBoardErrorHint")}
                              </Typography>
                              <Button
                                size="small"
                                color="error"
                                startIcon={<ReplayIcon />}
                                onClick={() => retryIds([row.dominio_id])}
                                sx={{ textTransform: "none" }}
                              >
                                {t("platformPage.waDailyBoardRetryOne")}
                              </Button>
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {t("platformPage.waDailyBoardPending")}
                            </Typography>
                          )}
                        </TableCell>
                      ) : (
                        <>
                          <TableCell
                            align="right"
                            title={
                              row.status === "ok"
                                ? row.baseline === "reference_period" && referenceMonthLabel
                                  ? t("platformPage.waDailyBoardBaseHintReference", {
                                      month: boardMonthLabel || "—",
                                      refMonth: referenceMonthLabel,
                                    })
                                  : t("platformPage.waDailyBoardBaseHint", {
                                      month: boardMonthLabel || "—",
                                    })
                                : undefined
                            }
                          >
                            {row.status === "ok" && row.projected_messages != null
                              ? fmtQty(row.projected_messages, locale)
                              : "—"}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 700 }}>
                            {row.status === "ok" && row.projected_amount_brl != null
                              ? fmtMoney(row.projected_amount_brl, locale)
                              : "—"}
                          </TableCell>
                          <TableCell align="right">
                            {row.status === "ok" ? fmtQty(row.avg_daily_messages, locale) : "—"}
                          </TableCell>
                          {days.map((day) => {
                            const date = day.date;
                            const cell = qtyByWorkspaceDay(row, date);
                            if (row.status !== "ok" || !cell) {
                              return (
                                <TableCell key={date} align="right" sx={{ color: "text.disabled" }}>
                                  —
                                </TableCell>
                              );
                            }
                            return (
                              <TableCell
                                key={date}
                                align="right"
                                sx={estimateSx(cell.projected)}
                                title={
                                  cell.projected
                                    ? t("platformPage.waDailyBoardProjectedMark")
                                    : t("platformPage.waDailyBoardLegendActual")
                                }
                              >
                                {fmtQty(cell.qty, locale)}
                              </TableCell>
                            );
                          })}
                        </>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      ) : null}
    </Stack>
  );
}
